import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { API } from "../../App";
import { epochLabel } from "./anacStatus";

export const ANAC_EXAMS = [
  { value: "caderno_1", label: "Caderno I" },
  { value: "caderno_2", label: "Caderno II" },
  { value: "comunicacoes", label: "Comunicações" },
];
const RESULT_LABEL = { agendado: "Marcado", aprovado: "Aprovado", reprovado: "Reprovado", faltou: "Faltou", justificado: "Justificado (ANAC)" };
const RESULT_CLASS = {
  agendado: "text-sky-300 border-sky-500/40",
  aprovado: "text-emerald-300 border-emerald-500/40",
  reprovado: "text-red-300 border-red-500/40",
  faltou: "text-red-300 border-red-500/40",
  justificado: "text-amber-300 border-amber-500/40",
};
const examLabel = (kind) => ANAC_EXAMS.find((e) => e.value === kind)?.label || kind;
const fmtDay = (iso) => iso.split("-").reverse().join("/");

// Teóricas ANAC de um aluno: o instrutor regista, corrige e controla as tentativas (ver services/anac_theory.py)
export default function AnacTheoryDialog({ student, open, onClose }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [practical, setPractical] = useState("");
  const [grades, setGrades] = useState({});
  // estado: marcado | feito (com nota) | faltou | justificado (atestado médico aprovado pela ANAC)
  const [form, setForm] = useState({ exam_kind: "caderno_1", exam_date: "", state: "marcado", score: "", notes: "" });

  const load = async () => {
    if (!student?.user_id) return;
    try {
      const r = await axios.get(`${API}/anac-theory/students/${student.user_id}`, { withCredentials: true });
      setData(r.data);
      setPractical(r.data.practical_exam_date || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a carregar as teóricas"); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) load(); }, [open, student?.user_id]);

  const create = async () => {
    if (!form.exam_date) { toast.error("Indique a data do exame."); return; }
    if (form.state === "feito" && form.score === "") { toast.error("Indique a nota (em percentagem) do exame feito."); return; }
    if (form.state === "justificado" && !form.notes.trim()) { toast.error("Indique a justificação (atestado médico aprovado pela ANAC)."); return; }
    setSaving(true);
    try {
      const isAbsence = form.state === "faltou" || form.state === "justificado";
      await axios.post(`${API}/anac-theory/students/${student.user_id}/records`, {
        exam_kind: form.exam_kind,
        exam_date: form.exam_date,
        scheduled: form.state === "marcado",
        score: form.state === "feito" ? Number(form.score) : null,
        absence: isAbsence ? form.state : null,
        notes: form.notes || "",
      }, { withCredentials: true });
      toast.success(form.state === "marcado" ? "Exame marcado" : "Registado");
      setForm({ ...form, exam_date: "", score: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a registar"); }
    finally { setSaving(false); }
  };

  const grade = async (rec) => {
    const score = grades[rec.id];
    if (score === undefined || score === "") { toast.error("Indique a nota (em percentagem)."); return; }
    try {
      await axios.put(`${API}/anac-theory/records/${rec.id}`, { score: Number(score) }, { withCredentials: true });
      toast.success("Resultado registado");
      setGrades({ ...grades, [rec.id]: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a registar"); }
  };

  // Falta num exame marcado: «faltou» conta como ida; «justificado» (atestado aprovado pela ANAC) não conta
  const markAbsence = async (rec, absence) => {
    let notes;
    if (absence === "justificado") {
      notes = window.prompt("Referência do atestado médico aprovado pela ANAC:");
      if (!notes || !notes.trim()) return;
    }
    try {
      await axios.put(`${API}/anac-theory/records/${rec.id}`, { absence, notes }, { withCredentials: true });
      toast.success(absence === "faltou" ? "Falta registada (conta como ida)" : "Falta justificada registada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a registar"); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Apagar o exame marcado de ${examLabel(rec.exam_kind)} (${rec.exam_date})?`)) return;
    try {
      await axios.delete(`${API}/anac-theory/records/${rec.id}`, { withCredentials: true });
      toast.success("Exame marcado apagado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a apagar"); }
  };

  const savePractical = async (value) => {
    try {
      await axios.put(`${API}/anac-theory/students/${student.user_id}/practical`, { exam_date: value || null }, { withCredentials: true });
      toast.success(value ? "Data do exame prático guardada" : "Data do exame prático removida");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro a guardar"); }
  };

  const u = data?.usage;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="anac-theory-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Teóricas ANAC · {student?.name}</DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="py-6 text-center text-slate-500 text-sm">A carregar...</div>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Aviso só: a data de início do curso define-se no formulário da turma (Turmas > Editar turma) */}
            {data.class && !data.class.course_start_date && (
              <div className="text-xs text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded p-2" data-testid="anac-course-start-missing">
                Falta a data de início do curso da turma {data.class.name}. Define-a em Turmas, no botão Editar turma.
              </div>
            )}

            {/* Contadores: idas à ANAC e tentativas por exame */}
            <div className="rounded-md border border-slate-700 p-3" data-testid="anac-usage">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-semibold text-slate-100">Sittings: {u.seats_used} de {u.seats_max}</span>
                {u.needs_new_course && <span className="text-xs px-2 py-0.5 rounded border border-red-500/50 text-red-300">Sem sittings: tem de fazer novo curso</span>}
                {u.all_approved && <span className="text-xs px-2 py-0.5 rounded border border-emerald-500/50 text-emerald-300">Teóricas concluídas</span>}
              </div>
              <div className="mt-2 text-xs text-slate-300 space-y-0.5" data-testid="anac-deadlines">
                {u.theory_deadline && (
                  <div>Prazo para completar as provas: {fmtDay(u.theory_deadline)}{u.expired && <span className="text-red-300"> · ultrapassado: as aprovadas caducaram</span>}</div>
                )}
                {u.licence_valid_until && <div>Validade das provas para a licença: {fmtDay(u.licence_valid_until)}</div>}
                {data.course && <div>Fim do prazo do curso (2 anos): {fmtDay(data.course.deadline)} · Cartão de aluno válido até: {data.cartao_aluno_valid_until ? fmtDay(data.cartao_aluno_valid_until) : "não registado"}</div>}
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-300">
                {ANAC_EXAMS.map((e) => {
                  const ex = u.exams[e.value];
                  return (
                    <div key={e.value} className="rounded border border-slate-800 p-2">
                      <div className="text-slate-200">{e.label}</div>
                      <div>Tentativas: {ex.attempts_used} de {ex.attempts_max}</div>
                      <div className={ex.approved ? "text-emerald-300" : "text-slate-500"}>{ex.approved ? "Aprovado" : "Não aprovado"}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Registos */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Registos</div>
              {data.records.length === 0 ? (
                <div className="text-slate-500 text-xs italic">Ainda sem exames registados.</div>
              ) : (
                <div className="space-y-2">
                  {data.records.map((rec) => (
                    <div key={rec.id} className="rounded border border-slate-800 p-2 flex items-center justify-between gap-2 flex-wrap" data-testid="anac-record">
                      <div className="min-w-0">
                        <div className="text-slate-100">
                          {examLabel(rec.exam_kind)} · {fmtDay(rec.exam_date)}
                          <span className="text-slate-400 text-xs"> · época {epochLabel(rec.exam_date)}</span>
                        </div>
                        {rec.score != null && <div className="text-xs text-slate-400">Nota: {rec.score}%</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded border ${RESULT_CLASS[rec.result]}`}>{RESULT_LABEL[rec.result]}</span>
                        {rec.result === "agendado" && (
                          <>
                            <Input type="number" min={0} max={100} placeholder="Nota %" className="w-24 h-8 bg-slate-800 border-slate-700"
                              value={grades[rec.id] ?? ""} onChange={(e) => setGrades({ ...grades, [rec.id]: e.target.value })} />
                            <Button size="sm" className="h-8" onClick={() => grade(rec)}>Registar resultado</Button>
                            <Button size="sm" variant="outline" className="h-8 border-red-500/40 text-red-300" onClick={() => markAbsence(rec, "faltou")}>Faltou</Button>
                            <Button size="sm" variant="outline" className="h-8 border-amber-500/40 text-amber-300" onClick={() => markAbsence(rec, "justificado")}>Justificado</Button>
                            <Button size="sm" variant="outline" className="h-8 border-red-500/40 text-red-300" onClick={() => remove(rec)}>Apagar</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Novo registo */}
            <div className="rounded-md border border-slate-700 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">Novo registo</div>
              <div className="grid grid-cols-2 gap-2">
                <select className="bg-slate-800 border border-slate-700 rounded px-2 h-9 text-slate-100" value={form.exam_kind}
                  onChange={(e) => setForm({ ...form, exam_kind: e.target.value })} data-testid="anac-form-kind">
                  {ANAC_EXAMS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
                <select className="bg-slate-800 border border-slate-700 rounded px-2 h-9 text-slate-100" value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="anac-form-state">
                  <option value="marcado">Marcado na ANAC</option>
                  <option value="feito">Já feito (com nota)</option>
                  <option value="faltou">Faltou (conta como ida)</option>
                  <option value="justificado">Falta justificada (atestado ANAC)</option>
                </select>
                <Input type="date" className="bg-slate-800 border-slate-700" value={form.exam_date} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} data-testid="anac-form-date" />
                {form.state === "feito" && (
                  <Input type="number" min={0} max={100} placeholder="Nota % (75 ou mais aprova)" className="bg-slate-800 border-slate-700" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} data-testid="anac-form-score" />
                )}
                {form.state === "justificado" && (
                  <Input placeholder="Atestado médico aprovado pela ANAC (referência)" className="bg-slate-800 border-slate-700" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="anac-form-justification" />
                )}
              </div>
              <div className="flex justify-end">
                <Button disabled={saving} onClick={create} data-testid="anac-form-save">{saving ? "A guardar..." : "Guardar"}</Button>
              </div>
            </div>

            {/* Exame prático */}
            <div className="rounded-md border border-slate-700 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Exame prático (depois das aulas práticas)</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input type="date" className="bg-slate-800 border-slate-700 w-44" value={practical} onChange={(e) => setPractical(e.target.value)} data-testid="anac-practical-date" />
                <Button size="sm" onClick={() => savePractical(practical)}>Guardar</Button>
                {data.practical_exam_date && <Button size="sm" variant="outline" onClick={() => savePractical("")}>Remover</Button>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
